// Invite wizard step 2 — when and where. RON-5466 Phase 2. Prototype: `InviteStep2`.
//
// Uses the app's `CustomDatePicker` and `CustomSearchableDropdown` rather than the prototype's
// raw `<input type="date">` / `<select>`, so the fields match every other booking screen, and the
// house `.form-row` field markup via VisitorFormRow.

import { useTranslation } from "react-i18next";

import CustomDatePicker from "../../Common/CustomDatePicker.jsx";
import CustomSearchableDropdown from "../../Common/CustomSearchableDropdown.jsx";
import VisitorFormRow, { fieldClassName } from "./VisitorFormRow.jsx";
import VisitorResourcePicker from "./VisitorResourcePicker.jsx";
import { fromISODate, toISODate } from "../visitorDateUtils.js";

const VisitDetailsStep = ({
  form,
  sites,
  errors,
  onField,
  canAssignDesk,
  canAssignParking,
}) => {
  const { t } = useTranslation("common");

  const selectedSite = sites.find((site) => site.id === form.siteId) || null;
  const buildings = selectedSite?.buildings || [];

  // Prototype rule: the picker only appears when a site has MORE than one building. A site with
  // exactly one still records it (set when the site changes), and a site with none leaves it null.
  const showBuildingPicker = buildings.length > 1;

  return (
    <>
      <div className="visitor-card">
        <p className="visitor-card__title">{t("react_visitors_visit_title")}</p>

        <div className="visitor-field-grid">
          <VisitorFormRow
            id="visitor-start-date"
            label={t("react_visitors_field_start_date")}
          >
            <CustomDatePicker
              id="visitor-start-date"
              selected={fromISODate(form.startDate)}
              minDate={new Date()}
              onChange={(date) => onField("startDate", toISODate(date))}
            />
          </VisitorFormRow>

          {/* The end date carries an error slot because the contractor wizard gates on it: a visit
              that runs past the clearance is refused here. Without this the refusal was silent —
              Next simply stopped working. */}
          <VisitorFormRow
            id="visitor-end-date"
            label={t("react_visitors_field_end_date")}
            error={errors?.endDate ? t(errors.endDate) : null}
          >
            <CustomDatePicker
              id="visitor-end-date"
              selected={fromISODate(form.endDate)}
              // Cannot end before it starts; the wizard also pushes the end forward when the
              // start moves past it.
              minDate={fromISODate(form.startDate) || new Date()}
              onChange={(date) => onField("endDate", toISODate(date))}
            />
          </VisitorFormRow>
        </div>

        <div className="visitor-field-grid">
          <VisitorFormRow
            id="visitor-arrival-time"
            label={t("react_visitors_field_arrival")}
          >
            <input
              id="visitor-arrival-time"
              type="time"
              // No success tick: the native time input already owns its right edge.
              className={fieldClassName(form.arrivalTime, null, false)}
              value={form.arrivalTime}
              onChange={(event) => onField("arrivalTime", event.target.value)}
            />
          </VisitorFormRow>

          {/* REMOVED 07-October-2026 — `expected_out` was here, and the prototype never had it.
              It was added for the RON-5466 DoD (it drives the checkout-reminder email and overstay
              status), and the Laravel team added the column on 23-September at our request.

              Taken out by product decision: the invite flows do not ask for it. The field is
              OPTIONAL on the wire (`expected_out?`) and the mapper only ever sent it when set, so
              nothing here sends it now and no create or patch is refused for its absence.

              Reception still uses it — its walk-in modal, detail panel and CSV export all read the
              same column. This removal is scoped to the invite screens only. */}
        </div>

        <VisitorFormRow label={t("react_visitors_field_site")}>
          <CustomSearchableDropdown
            items={sites}
            value={form.siteId}
            labelKey="name"
            valueKey="id"
            searchable={false}
            placeholder={t("react_visitors_field_site")}
            onChange={(siteId) => onField("siteId", siteId)}
          />
        </VisitorFormRow>

        {showBuildingPicker && (
          <VisitorFormRow label={t("react_visitors_field_building")}>
            <CustomSearchableDropdown
              items={buildings}
              value={form.buildingId}
              labelKey="name"
              valueKey="id"
              searchable={false}
              placeholder={t("react_visitors_field_building")}
              onChange={(buildingId) => onField("buildingId", buildingId)}
            />
          </VisitorFormRow>
        )}

        <VisitorFormRow
          id="visitor-host"
          label={`${t("react_visitors_field_host")}*`}
          error={errors?.host ? t(errors.host) : null}
        >
          <input
            id="visitor-host"
            type="text"
            placeholder={`${t("react_visitors_field_host")}*`}
            className={fieldClassName(form.host, errors?.host)}
            value={form.host}
            onChange={(event) => onField("host", event.target.value)}
          />
        </VisitorFormRow>
      </div>

      <VisitorResourcePicker
        startDate={form.startDate}
        endDate={form.endDate}
        value={form.resources}
        onChange={(resources) => onField("resources", resources)}
        canAssignDesk={canAssignDesk}
        canAssignParking={canAssignParking}
      />
    </>
  );
};

export default VisitDetailsStep;
